import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  PendencyCatBadge,
  PendencyCatIcon,
  PendencyLifecycleBadge,
  PendencyOriginBadge,
} from './PendencyBadges';

import type { PendencyWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

export type PendencyDueState = 'overdue' | 'due-soon' | 'normal';

/** Ações inline do card — variam por estado (§8.4 do mockup). */
export type PendencyActionKind = 'progress' | 'postpone' | 'resolve' | 'resume' | 'outcome';

const DUE_CARD_CLASS: Record<PendencyDueState, string> = {
  overdue: 'pendency-card--overdue',
  'due-soon': 'pendency-card--due-soon',
  normal: '',
};

const DUE_LABEL_CLASS: Record<PendencyDueState, string> = {
  overdue: 'pendency-card__due--overdue',
  'due-soon': 'pendency-card__due--due-soon',
  normal: '',
};

function formatDue(deadline: string | null, dueState: PendencyDueState): string {
  if (deadline === null) return 'Sem prazo';
  const short = new Date(deadline).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
  return dueState === 'overdue' ? `Vencida · ${short}` : short;
}

interface ActionDef {
  key: PendencyActionKind;
  label: string;
  primary?: boolean;
}

function actionsFor(status: PendencyWireItem['status']): ActionDef[] {
  switch (status) {
    case 'open':
      return [
        { key: 'progress', label: 'Em curso' },
        { key: 'postpone', label: 'Adiar' },
        { key: 'resolve', label: 'Resolver', primary: true },
      ];
    case 'in_progress':
      return [
        { key: 'resolve', label: 'Resolver', primary: true },
        { key: 'postpone', label: 'Adiar' },
      ];
    case 'postponed':
      return [
        { key: 'resume', label: 'Retomar', primary: true },
        { key: 'resolve', label: 'Resolver' },
      ];
    case 'resolved':
      return [{ key: 'outcome', label: 'Ver desfecho' }];
    default:
      return [];
  }
}

/**
 * PendencyCard — card da caixa do atendente. Porta o `.pendency-card` do
 * mockup. Campos reais apenas: SEM subject/target/person fabricados (§4 spec).
 */
export function PendencyCard({
  item,
  dueState,
  onOpen,
  onAction,
}: {
  item: PendencyWireItem;
  dueState: PendencyDueState;
  onOpen: (item: PendencyWireItem) => void;
  onAction: (kind: PendencyActionKind, item: PendencyWireItem) => void;
}): JSX.Element {
  const terminalCls =
    item.status === 'resolved'
      ? 'pendency-card--resolved'
      : item.status === 'cancelled' || item.status === 'expired'
        ? 'pendency-card--cancelled'
        : item.status === 'archived'
          ? 'pendency-card--archived'
          : '';
  const actions = actionsFor(item.status);
  const navigate = useNavigate();
  const convId = item.conversationId;

  return (
    <div
      className={`pendency-card ${DUE_CARD_CLASS[dueState]} ${terminalCls}`.trim()}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(item);
        }
      }}
    >
      <div className="pendency-card__head">
        <PendencyCatIcon category={item.category} />
        <div className="pendency-card__title-wrap">
          <h3 className="pendency-card__title">{item.title}</h3>
          {convId !== null && (
            <div className="pendency-card__context">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/atendimento/conversation/${convId}`);
                }}
              >
                Ver conversa
              </button>
            </div>
          )}
        </div>
        <span className={`pendency-card__due ${DUE_LABEL_CLASS[dueState]}`.trim()}>
          {formatDue(item.deadline, dueState)}
        </span>
      </div>

      <div className="pendency-card__badges">
        <PendencyLifecycleBadge status={item.status} postponeCount={item.postponeCount} />
        <PendencyCatBadge category={item.category} />
        <PendencyOriginBadge source={item.source} />
      </div>

      {item.description !== null && item.description.trim().length > 0 && (
        <p className="pendency-card__desc">{item.description}</p>
      )}

      <div className="pendency-card__actions">
        {actions.map((a) => (
          <button
            key={a.key}
            type="button"
            className={`btn btn--sm ${a.primary === true ? 'btn--primary' : 'btn--secondary'}`}
            onClick={(e) => {
              e.stopPropagation();
              onAction(a.key, item);
            }}
          >
            {a.label}
          </button>
        ))}
        <button
          type="button"
          className="btn btn--sm btn--ghost"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(item);
          }}
        >
          Detalhe
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
